import React from 'react';

const SPARKLINE_WIDTH = 100;
const SPARKLINE_HEIGHT = 24;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  miniCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    transition: 'box-shadow 0.2s ease',
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1f2937',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
  currentAvg: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1f2937',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  changePercent: {
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
  sparklineRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rangeText: {
    fontSize: '11px',
    color: '#6b7280',
  },
  badge: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    display: 'inline-block',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

function Sparkline({ values, color }) {
  if (!values || values.length < 2) {
    return (
      <svg
        width={SPARKLINE_WIDTH}
        height={SPARKLINE_HEIGHT}
        viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
      >
        <line
          x1="0"
          y1={SPARKLINE_HEIGHT / 2}
          x2={SPARKLINE_WIDTH}
          y2={SPARKLINE_HEIGHT / 2}
          stroke="#d1d5db"
          strokeWidth="1"
          strokeDasharray="2 2"
        />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const paddingY = 2;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * SPARKLINE_WIDTH;
    const y = paddingY + ((max - v) / range) * (SPARKLINE_HEIGHT - paddingY * 2);
    return `${x},${y}`;
  });

  return (
    <svg
      width={SPARKLINE_WIDTH}
      height={SPARKLINE_HEIGHT}
      viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
    >
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChangeArrow({ value }) {
  if (value > 0) {
    return (
      <span style={{ ...styles.changePercent, color: '#dc2626' }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 2L10 7H2L6 2Z" fill="#dc2626" />
        </svg>
        +{value.toFixed(1)}%
      </span>
    );
  }
  if (value < 0) {
    return (
      <span style={{ ...styles.changePercent, color: '#16a34a' }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 10L2 5H10L6 10Z" fill="#16a34a" />
        </svg>
        {value.toFixed(1)}%
      </span>
    );
  }
  return (
    <span style={{ ...styles.changePercent, color: '#6b7280' }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="2.5" fill="#6b7280" />
      </svg>
      0.0%
    </span>
  );
}

function formatPrice(value) {
  if (value == null) return '--';
  return `$${Number(value).toFixed(2)}`;
}

function MiniStatCard({ item }) {
  const {
    label,
    currentAvg,
    changePercent,
    sparkline,
    min,
    max,
    addressCount,
    color,
  } = item;

  return (
    <div
      style={{
        ...styles.miniCard,
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div style={styles.fieldLabel}>{label}</div>

      <div style={styles.priceRow}>
        <span style={styles.currentAvg}>{formatPrice(currentAvg)}</span>
        <ChangeArrow value={changePercent} />
      </div>

      <div style={styles.sparklineRow}>
        <Sparkline values={sparkline} color={color} />
      </div>

      <div style={styles.metaRow}>
        <span style={styles.rangeText}>
          {formatPrice(min)} &ndash; {formatPrice(max)}
        </span>
        <span
          style={{
            ...styles.badge,
            background: `${color}14`,
            color: color,
          }}
        >
          {addressCount} addr
        </span>
      </div>
    </div>
  );
}

export default function MiniDashboard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Field-Level KPIs</div>
        <div style={styles.subtitle}>
          Per-service pricing metrics at a glance
        </div>
        <div style={styles.emptyState}>No field KPI data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Field-Level KPIs</div>
      <div style={styles.subtitle}>
        Per-service pricing metrics at a glance
      </div>

      <div style={styles.grid} data-mini-dashboard-grid="">
        {data.map((item) => (
          <MiniStatCard key={item.field} item={item} />
        ))}
      </div>

      <style>{`
        @media (max-width: 1024px) {
          div[data-mini-dashboard-grid] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          div[data-mini-dashboard-grid] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
